/**
 * Video Component
 * 
 * A component for displaying videos with a custom play button and thumbnail.
 */

export interface IVideoOptions {
    /** The title of the video to display above it */
    title?: string;
    /** URL of the video thumbnail image */
    thumbnailSrc: string;
    /** URL of the video file to play */
    videoSrc: string;
    /** Alternative text for the thumbnail image */
    alt?: string;
    /** Aria label for the play button */
    playAriaLabel?: string;
}

/**
 * Video Component
 * 
 * @param {HTMLElement} container - The container element to render the component in
 * @param {IVideoOptions} options - The options for configuring the component
 * @returns {HTMLElement} - The container element with the component rendered into it
 */
export class Video {
    private element: HTMLElement;
    private playButton: HTMLButtonElement;
    private video: HTMLVideoElement;
    private component: HTMLElement;
    private isVideoPlaying = false;

    constructor(element: HTMLElement) {
        this.element = element
        this.playButton = this.element.querySelector('.video-component__play-button') as HTMLButtonElement;
        this.video = this.element.querySelector('.video-component__video') as HTMLVideoElement;
        this.component = this.element.querySelector('.video-component') as HTMLElement;

        this.init();
    }

    private init(): void {
        if (this.playButton && this.video) {
            this.playButton.addEventListener('click', () => {
                this.component.classList.add('playing');
                this.video.classList.add('active');
                this.isVideoPlaying = true;
                // Use the Promise API for better browser compatibility 
                const playPromise = this.video.play();

                if (playPromise !== undefined) {
                    playPromise.catch(error => {
                        console.error('Error playing video:', error);
                        this.isVideoPlaying = false;
                    });
                }
            });


            // Custom video event listener to prevent delay when clicking video on Chrome
            this.video.addEventListener('click', (event) => {
                event.preventDefault();
                // Toggle play/pause based on our tracked state instead of video.paused
                if (this.isVideoPlaying) {
                    this.video.pause();
                    this.isVideoPlaying = false;
                } else {
                    this.video.play();
                    this.isVideoPlaying = true;
                }
            });

            // Safari handles controls differently, so we need a separate approach
            // Add event listeners for play and pause
            this.video.addEventListener('play', () => {
                this.component.classList.add('playing');
                this.video.classList.add('active');
            });

            // When pause or video ends, ensure consistent state
            this.video.addEventListener('pause', () => {
                // Only reset if video has ended
                if (this.video.currentTime >= this.video.duration) {
                    this.component.classList.remove('playing');
                    this.video.classList.remove('active');
                }
            });

            // When video ends, show the thumbnail again
            this.video.addEventListener('ended', () => {
                this.component.classList.remove('playing');
                this.video.classList.remove('active');
            });
        }
    };
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.video-section').forEach(video => {
        new Video(video as HTMLElement);
    });
});